// Fonte única da busca + normalização + seed no store dos planos (getPlansType).
// Antes essa lógica vivia inline em layouts/default.vue e só rodava no cliente
// (onBeforeMount), deixando os preços fora do HTML servido (SSR). Extraída aqui
// para ser chamada tanto no SSR (página /assine, via useAsyncData) quanto no
// cliente (layout), sem divergência.
import api from "@/api";

export interface PlansTypeParams {
  language: string;
  coupon: string;
  voucher: string;
  code_share: string;
}

// Resolve language/coupon/voucher/code_share do mesmo jeito que o getPlansType
// original: query da URL primeiro, com fallback no store persistido, e a mesma
// exclusão mútua entre coupon e voucher.
export function resolvePlansTypeParams(): PlansTypeParams {
  const route = useRoute();
  const storePlan = planStore();
  const language = String(route.params.language ?? "");
  const voucher = route.query.coupon
    ? ""
    : (route.query.voucher as string) || (storePlan.voucher as string) || "";
  const coupon = route.query.voucher
    ? ""
    : (route.query.coupon as string) || (storePlan.coupon as string) || "";
  const code_share = String(route.query.code_share ?? "");
  return { language, coupon, voucher, code_share };
}

// Busca os planos, ordena (plan_type_id DESC; months DESC) e semeia todos os
// campos do store — idêntico ao getPlansType original. Retorna a lista ordenada
// (o useAsyncData precisa de um retorno serializável e não-undefined).
export async function fetchAndSeedPlansType(params: PlansTypeParams) {
  const storePlan = planStore();
  const { language, coupon, voucher, code_share } = params;

  // No SSR o localeStore.locale é "" (só é populado no cliente via /api/get-locale).
  // Sem isto, o setPlansType→setLanguagePlans gravaria languagePlans="" e o guard
  // do onBeforeMount no layout ("pt-br" !== "") dispararia um re-fetch redundante
  // no cliente. Alinhar o locale ao idioma da rota evita isso.
  if (language) localeStore().setLocale(language);

  const { data } = await api.get(
    `v1/home/getPlansType?language=${language ?? ""}&coupon=${coupon}` +
      `&voucher=${voucher}&code_share=${code_share ?? ""}`
  );

  storePlan.setSaldoUpgrade(data.saldoUpgrade);
  storePlan.setCardOnlyPromotion(data.cardOnlyPromotion ?? false);
  storePlan.setParcelamentoDisponivel(data.parcelamento ?? 1);
  storePlan.setVoucher(voucher);
  storePlan.setCoupon(coupon);
  storePlan.setCloseBanner(false);

  let ordernarPlans = data.plans.sort((a: any, b: any) => {
    if (a.plan_type_id < b.plan_type_id) {
      return 1;
    }
    if (a.plan_type_id > b.plan_type_id) {
      return -1;
    }
    return 0;
  });

  ordernarPlans = ordernarPlans.map((item: any) => {
    item.plans.sort((a: any, b: any) => {
      if (a.months < b.months) {
        return 1;
      }
      if (a.months > b.months) {
        return -1;
      }
      return 0;
    });
    return item;
  });

  storePlan.setPlansType(ordernarPlans);
  storePlan.setDownloadLimit(
    Number(data.dailyDownloadLimit) > 0 ? String(data.dailyDownloadLimit) : "5"
  );

  return ordernarPlans;
}
